import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  LinearProgress,
  Stack,
  Fade,
  Grow,
  Zoom,
  Pagination,
  useTheme,
  alpha,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import {
  Search,
  Refresh,
  Person,
  TrendingUp,
  Token,
  Block,
  Flag,
  CheckCircle,
  Star,
  WorkspacePremium,
  MoneyOff,
  BarChart,
  Timeline,
  Api,
  ArrowBack,
  Visibility,
  Edit,
  PeopleAlt,
  AdminPanelSettings,
  Security,
  Group,
  Speed,
  Assessment,
  Insights,
  AutoAwesome,
  ExpandMore,
  ExpandLess,
  MouseOutlined,
  KeyboardArrowRight
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface UserRow {
  id: number;
  email: string;
  role: string;
  plan: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  preferences?: Record<string, any>;
  // Token quota fields (fetched alongside users)
  token_quota_monthly?: number | null;
  tokens_used_quota?: number;
  quota_count_from?: string | null;
}

interface Overview {
  user_stats: { total: number; active: number; paid: number; enterprise: number; free: number };
  plan_breakdown: Array<{ plan: string; count: number }>;
  top_users_last_7_days: Array<{
    id: number;
    email: string;
    role: string;
    plan: string;
    requests: number;
    tokens: number;
    blocked: number;
  }>;
  platform_daily_activity: Array<{
    date: string;
    requests: number;
    tokens: number;
    blocked: number;
  }>;
}

interface ActivityItem {
  id: number;
  prompt_text: string;
  response_text?: string;
  filtered_prompt?: string;
  filtered_response?: string;
  status: string;
  threat_score: number;
  detected_threats?: any[];
  tokens_used: number;
  model?: string;
  processing_time_ms?: number;
  request_ip?: string;
  user_agent?: string;
  api_key_id?: number;
  api_key_name?: string;
  endpoint_used?: string;
  pii_redacted?: any;
  estimated_cost_usd: number;
  metadata?: Record<string, any>;
  created_at: string;
}

interface UserUsage {
  user: {
    id: number;
    email: string;
    role: string;
    plan: string;
    is_active: boolean;
    created_at: string;
    last_login?: string;
  };
  period_days: number;
  overall: {
    total_requests: number;
    total_tokens: number;
    avg_threat_score: number;
    max_threat_score: number;
    blocked: number;
    flagged: number;
    allowed: number;
    redacted: number;
    avg_processing_ms: number;
    max_processing_ms: number;
    active_days: number;
    unique_api_keys_used: number;
  };
  last_7_days: { total_requests: number; total_tokens: number; blocked: number; flagged: number };
  model_breakdown: Array<{
    model: string;
    requests: number;
    tokens: number;
    avg_threat_score: number;
  }>;
  daily_activity: Array<{
    date: string;
    requests: number;
    tokens: number;
    blocked: number;
    flagged: number;
    allowed: number;
    avg_threat_score: number;
  }>;
  api_keys: Array<{
    id: number;
    name: string;
    is_active: boolean;
    usage_count: number;
    last_used?: string;
    rate_limit: number;
  }>;
}

const PLAN_CHIP_COLOR: Record<
  string,
  'default' | 'success' | 'warning' | 'error' | 'primary' | 'secondary' | 'info'
> = {
  free: 'default',
  paid: 'success',
  enterprise: 'primary'
};

const getRoleColor = (
  role: string
): 'default' | 'error' | 'warning' | 'info' | 'primary' | 'success' | 'secondary' => {
  switch (role) {
    case 'super_admin':
      return 'error';
    case 'admin':
      return 'error';
    case 'moderator':
      return 'warning';
    case 'team_lead':
      return 'info';
    case 'department_head':
      return 'primary';
    default:
      return 'default';
  }
};

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'super_admin':
    case 'admin':
      return <AdminPanelSettings fontSize="small" />;
    case 'moderator':
      return <Security fontSize="small" />;
    case 'team_lead':
    case 'department_head':
      return <Group fontSize="small" />;
    default:
      return <Person fontSize="small" />;
  }
};

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [planDialog, setPlanDialog] = useState<{
    open: boolean;
    userId: number;
    email: string;
    currentPlan: string;
  } | null>(null);
  const [newPlan, setNewPlan] = useState('');
  const [planUpdating, setPlanUpdating] = useState(false);

  // Token quota state
  const [quotaDialog, setQuotaDialog] = useState<{
    open: boolean;
    userId: number;
    email: string;
    currentQuota: number | null;
    usedThisMonth: number;
    resetsAt: string;
    countFrom: string | null;
  } | null>(null);
  const [newQuota, setNewQuota] = useState('');
  const [quotaUpdating, setQuotaUpdating] = useState(false);
  const [quotaLoading, setQuotaLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setOverviewLoading(true);
      const { data } = await axios.get('/api/super-admin/overview');
      setOverview(data);
    } catch {
      toast.error('Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(
    async (page = 1) => {
      try {
        setUsersLoading(true);
        const params: Record<string, any> = { page, limit: 20 };
        if (search) params.search = search;
        if (roleFilter) params.role = roleFilter;
        const { data } = await axios.get('/api/super-admin/users', { params });

        const baseUsers = data.users.map((u: any) => ({
          ...u,
          plan: u.preferences?.plan || 'free'
        }));

        // Fetch quota info for all users in parallel (silent — don't block table render)
        const quotaResults = await Promise.allSettled(
          baseUsers.map((u: any) => axios.get(`/api/super-admin/users/${u.id}/token-quota`))
        );

        const usersWithQuota = baseUsers.map((u: any, i: number) => {
          const q = quotaResults[i];
          if (q.status === 'fulfilled') {
            const d = q.value.data;
            return {
              ...u,
              token_quota_monthly: d.token_quota_monthly,
              tokens_used_quota: d.tokens_used_this_month,
              quota_count_from: d.count_from
            };
          }
          return u;
        });

        setUsers(usersWithQuota);
        setPagination(data.pagination);
      } catch {
        toast.error('Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    },
    [search, roleFilter]
  );

  useEffect(() => {
    fetchOverview();
    fetchUsers();
  }, [fetchOverview, fetchUsers]);

  const openUsage = async (userId: number) => {
    setUsageLoading(true);
    setSelectedUser(null);
    try {
      const { data } = await axios.get(`/api/super-admin/users/${userId}/usage?days=30`);
      setSelectedUser(data);
      setTab(2);
    } catch {
      toast.error('Failed to load user usage');
    } finally {
      setUsageLoading(false);
    }
  };

  const handlePlanUpdate = async () => {
    if (!planDialog || !newPlan) return;
    try {
      setPlanUpdating(true);
      await axios.patch(`/api/super-admin/users/${planDialog.userId}/plan`, { plan: newPlan });
      toast.success(`Plan updated to ${newPlan}`);
      setPlanDialog(null);
      fetchUsers(pagination.page);
      fetchOverview();
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setPlanUpdating(false);
    }
  };

  const openQuotaDialog = async (userId: number, email: string) => {
    setQuotaLoading(true);
    try {
      const { data } = await axios.get(`/api/super-admin/users/${userId}/token-quota`);
      setQuotaDialog({
        open: true,
        userId,
        email,
        currentQuota: data.token_quota_monthly,
        usedThisMonth: data.tokens_used_this_month,
        resetsAt: data.resets_at,
        countFrom: data.count_from || null
      });
      setNewQuota(data.token_quota_monthly ? String(data.token_quota_monthly) : '');
    } catch {
      toast.error('Failed to load token quota');
    } finally {
      setQuotaLoading(false);
    }
  };

  const handleQuotaUpdate = async () => {
    if (!quotaDialog) return;
    const quota = parseInt(newQuota) || 0;
    try {
      setQuotaUpdating(true);
      await axios.patch(`/api/super-admin/users/${quotaDialog.userId}/token-quota`, {
        token_quota_monthly: quota
      });
      toast.success(
        quota ? `Token quota set to ${quota.toLocaleString()}/month` : 'Token quota removed'
      );
      setQuotaDialog(null);
      fetchUsers(pagination.page);
    } catch {
      toast.error('Failed to update token quota');
    } finally {
      setQuotaUpdating(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Access denied. Super Admin only.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: { xs: 2.5, sm: 4 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
                borderRadius: '50%',
                transform: 'translate(30%, -30%)'
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box display="flex" alignItems="center" gap={2.5}>
                <Zoom in timeout={800}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 36, color: theme.palette.primary.main }} />
                  </Avatar>
                </Zoom>
                <Box>
                  <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    fontWeight={800}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    Super Admin Dashboard
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <AutoAwesome sx={{ fontSize: 16 }} />
                    Platform-wide user management, billing plans &amp; usage analytics
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  fetchOverview();
                  fetchUsers();
                }}
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
        </Fade>

        {/* Overview Stat Cards */}
        {overviewLoading ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          overview && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                {
                  label: 'Total Users',
                  value: overview.user_stats.total,
                  icon: <PeopleAlt sx={{ fontSize: 28 }} />,
                  color: theme.palette.primary.main
                },
                {
                  label: 'Active Users',
                  value: overview.user_stats.active,
                  icon: <CheckCircle sx={{ fontSize: 28 }} />,
                  color: theme.palette.success.main
                },
                {
                  label: 'Paid Users',
                  value: overview.user_stats.paid,
                  icon: <Star sx={{ fontSize: 28 }} />,
                  color: theme.palette.warning.main
                },
                {
                  label: 'Enterprise',
                  value: overview.user_stats.enterprise,
                  icon: <WorkspacePremium sx={{ fontSize: 28 }} />,
                  color: theme.palette.secondary.main
                }
              ].map((stat, i) => (
                <Grid item xs={6} md={3} key={stat.label}>
                  <Grow in timeout={600 + i * 100}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(stat.color, 0.06)} 100%)`,
                        border: `1px solid ${alpha(stat.color, 0.15)}`,
                        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 24px ${alpha(stat.color, 0.18)}`,
                          border: `1px solid ${alpha(stat.color, 0.35)}`
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              background: `linear-gradient(135deg, ${alpha(stat.color, 0.15)} 0%, ${alpha(stat.color, 0.25)} 100%)`,
                              border: `2px solid ${alpha(stat.color, 0.3)}`
                            }}
                          >
                            <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                          </Avatar>
                        </Box>
                        <Typography
                          variant="h4"
                          fontWeight={800}
                          sx={{ color: stat.color, letterSpacing: '-0.025em' }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontWeight: 600,
                            mt: 0.5,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          )
        )}

        {/* Main Content Tabs */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 1)}`,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 1)}`, px: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' }
                }}
              >
                <Tab
                  label="Users & Plans"
                  icon={<PeopleAlt fontSize="small" />}
                  iconPosition="start"
                />
                <Tab
                  label="User History"
                  icon={<BarChart fontSize="small" />}
                  iconPosition="start"
                />
                {selectedUser && (
                  <Tab
                    label={selectedUser.user.email.split('@')[0]}
                    icon={<Insights fontSize="small" />}
                    iconPosition="start"
                  />
                )}
              </Tabs>
            </Box>

            {/* Tab 0: Users & Plans */}
            {tab === 0 && (
              <Box sx={{ p: 3 }}>
                {/* Filters */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                    bgcolor: alpha(theme.palette.background.default, 0.5)
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={roleFilter}
                          label="Role"
                          onChange={(e) => setRoleFilter(e.target.value)}
                        >
                          <MenuItem value="">All Roles</MenuItem>
                          {[
                            'user',
                            'team_member',
                            'team_lead',
                            'department_head',
                            'admin',
                            'super_admin'
                          ].map((r) => (
                            <MenuItem key={r} value={r}>
                              {r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => fetchUsers(1)}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                      >
                        Search
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          borderColor: alpha(theme.palette.primary.main, 0.2)
                        }}
                      >
                        <Typography variant="h6" fontWeight={800} color="primary.main">
                          {pagination.total}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Total Users
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Users Table */}
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                    overflow: 'hidden',
                    '& .MuiTableRow-root:last-child .MuiTableCell-root': { borderBottom: 0 }
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          { label: 'User', width: '26%' },
                          { label: 'Role', width: '11%' },
                          { label: 'Plan', width: '10%' },
                          { label: 'Token Quota', width: '18%' },
                          { label: 'Status', width: '8%' },
                          { label: 'Joined / Login', width: '12%' },
                          { label: 'Actions', width: '15%' }
                        ].map((h) => (
                          <TableCell
                            key={h.label}
                            sx={{
                              width: h.width,
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.6px',
                              color: theme.palette.text.secondary,
                              bgcolor: alpha(theme.palette.background.default, 0.9),
                              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                              py: 1.75,
                              backdropFilter: 'blur(8px)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {h.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usersLoading ? (
                        // Skeleton rows
                        Array.from({ length: 6 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Skeleton variant="circular" width={38} height={38} />
                                <Box flex={1}>
                                  <Skeleton width="70%" height={16} />
                                  <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
                                </Box>
                              </Box>
                            </TableCell>
                            {[1, 2, 3, 4, 5, 6].map((j) => (
                              <TableCell key={j}>
                                <Skeleton height={24} width="80%" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                            <Avatar
                              sx={{
                                width: 72,
                                height: 72,
                                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                mx: 'auto',
                                mb: 2
                              }}
                            >
                              <PeopleAlt
                                sx={{
                                  fontSize: 40,
                                  color: alpha(theme.palette.text.secondary, 0.4)
                                }}
                              />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700} color="text.primary">
                              No users found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Try adjusting your search or filter criteria
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((u, idx) => {
                          const planColor =
                            u.plan === 'enterprise'
                              ? theme.palette.primary.main
                              : u.plan === 'paid'
                                ? theme.palette.success.main
                                : theme.palette.text.secondary;
                          const isEven = idx % 2 === 0;
                          return (
                            <TableRow
                              key={u.id}
                              sx={{
                                bgcolor: isEven
                                  ? 'transparent'
                                  : alpha(theme.palette.background.default, 0.45),
                                transition: 'background 0.15s ease',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  '& .row-actions': { opacity: 1 }
                                },
                                cursor: 'default'
                              }}
                            >
                              {/* User cell */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                    <Avatar
                                      sx={{
                                        width: 38,
                                        height: 38,
                                        bgcolor: u.is_active
                                          ? alpha(theme.palette.primary.main, 0.12)
                                          : alpha(theme.palette.text.secondary, 0.08),
                                        color: u.is_active
                                          ? theme.palette.primary.main
                                          : theme.palette.text.disabled,
                                        fontSize: 15,
                                        fontWeight: 800,
                                        border: `2px solid ${u.is_active ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.divider, 1)}`
                                      }}
                                    >
                                      {u.email[0].toUpperCase()}
                                    </Avatar>
                                    {/* Online dot */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: u.is_active
                                          ? theme.palette.success.main
                                          : theme.palette.text.disabled,
                                        border: `2px solid ${theme.palette.background.paper}`
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      noWrap
                                      sx={{ maxWidth: 200 }}
                                    >
                                      {u.email}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}
                                    >
                                      #{u.id}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>

                              {/* Role cell */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Chip
                                  icon={getRoleIcon(u.role)}
                                  label={u.role.replace(/_/g, ' ')}
                                  color={getRoleColor(u.role)}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                    fontSize: '0.72rem',
                                    height: 24
                                  }}
                                />
                              </TableCell>

                              {/* Plan cell — clickable */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Tooltip title="Click to change plan" placement="top" arrow>
                                  <Box
                                    onClick={() => {
                                      setPlanDialog({
                                        open: true,
                                        userId: u.id,
                                        email: u.email,
                                        currentPlan: u.plan
                                      });
                                      setNewPlan(u.plan);
                                    }}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      px: 1.25,
                                      py: 0.5,
                                      borderRadius: 1.5,
                                      cursor: 'pointer',
                                      border: `1.5px solid ${alpha(planColor, 0.35)}`,
                                      bgcolor: alpha(planColor, 0.08),
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        bgcolor: alpha(planColor, 0.16),
                                        border: `1.5px solid ${planColor}`,
                                        transform: 'scale(1.04)'
                                      }
                                    }}
                                  >
                                    <Box sx={{ color: planColor, display: 'flex', fontSize: 14 }}>
                                      {u.plan === 'enterprise' ? (
                                        <WorkspacePremium fontSize="inherit" />
                                      ) : u.plan === 'paid' ? (
                                        <Star fontSize="inherit" />
                                      ) : (
                                        <MoneyOff fontSize="inherit" />
                                      )}
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      sx={{
                                        color: planColor,
                                        textTransform: 'capitalize',
                                        lineHeight: 1
                                      }}
                                    >
                                      {u.plan}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </TableCell>

                              {/* Token Quota cell */}
                              <TableCell sx={{ py: 1.5 }}>
                                {u.token_quota_monthly && u.token_quota_monthly > 0 ? (
                                  (() => {
                                    const used = u.tokens_used_quota ?? 0;
                                    const limit = u.token_quota_monthly;
                                    const pct = Math.min(100, Math.round((used / limit) * 100));
                                    const exceeded = used >= limit;
                                    const barColor = exceeded
                                      ? 'error'
                                      : pct > 75
                                        ? 'warning'
                                        : 'success';
                                    return (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          mb={0.4}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: '0.7rem',
                                              fontWeight: 600,
                                              color: exceeded ? 'error.main' : 'text.secondary'
                                            }}
                                          >
                                            {used.toLocaleString()} / {limit.toLocaleString()}
                                          </Typography>
                                          {exceeded && (
                                            <Chip
                                              label="LIMIT"
                                              size="small"
                                              color="error"
                                              sx={{
                                                height: 16,
                                                fontSize: '0.6rem',
                                                fontWeight: 800,
                                                ml: 0.5,
                                                '& .MuiChip-label': { px: 0.75 }
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <LinearProgress
                                          variant="determinate"
                                          value={pct}
                                          color={barColor}
                                          sx={{
                                            height: 5,
                                            borderRadius: 3,
                                            bgcolor: alpha(theme.palette.divider, 1)
                                          }}
                                        />
                                      </Box>
                                    );
                                  })()
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ fontSize: '0.72rem' }}
                                  >
                                    No limit
                                  </Typography>
                                )}
                              </TableCell>

                              {/* Status cell */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.4,
                                    borderRadius: 1,
                                    bgcolor: u.is_active
                                      ? alpha(theme.palette.success.main, 0.1)
                                      : alpha(theme.palette.text.secondary, 0.08)
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: u.is_active
                                        ? theme.palette.success.main
                                        : theme.palette.text.disabled,
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                      color: u.is_active
                                        ? theme.palette.success.main
                                        : theme.palette.text.disabled,
                                      fontSize: '0.72rem'
                                    }}
                                  >
                                    {u.is_active ? 'Active' : 'Inactive'}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* Joined / Last Login — merged single cell */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.75rem', display: 'block' }}
                                  >
                                    {new Date(u.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: '2-digit'
                                    })}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={u.last_login ? 'text.disabled' : 'text.disabled'}
                                    sx={{ fontSize: '0.7rem', display: 'block' }}
                                  >
                                    {u.last_login
                                      ? new Date(u.last_login).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: '2-digit'
                                        })
                                      : 'Never logged in'}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* Actions — 3 icon-only buttons with tooltips */}
                              <TableCell sx={{ py: 1.5 }}>
                                <Box
                                  className="row-actions"
                                  display="flex"
                                  gap={0.75}
                                  alignItems="center"
                                  sx={{ opacity: { xs: 1, md: 0.7 }, transition: 'opacity 0.2s' }}
                                >
                                  <Tooltip title="View usage & analytics" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => openUsage(u.id)}
                                      aria-label="view usage analytics"
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: alpha(theme.palette.info.main, 0.1),
                                        color: theme.palette.info.main,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.info.main, 0.2),
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <BarChart sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={u.plan === 'free' ? 'Upgrade plan' : 'Change plan'}
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setPlanDialog({
                                          open: true,
                                          userId: u.id,
                                          email: u.email,
                                          currentPlan: u.plan
                                        });
                                        setNewPlan(u.plan);
                                      }}
                                      aria-label="change plan"
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: alpha(planColor, 0.1),
                                        color: planColor,
                                        border: `1px solid ${alpha(planColor, 0.25)}`,
                                        '&:hover': {
                                          bgcolor: alpha(planColor, 0.22),
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <Star sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Set monthly token quota" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => openQuotaDialog(u.id, u.email)}
                                      aria-label="set token quota"
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        color: theme.palette.warning.main,
                                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.warning.main, 0.2),
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <Token sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer: count + pagination */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={2.5}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Showing {users.length} of {pagination.total} users
                  </Typography>
                  {pagination.pages > 1 && (
                    <Pagination
                      count={pagination.pages}
                      page={pagination.page}
                      onChange={(_, p) => fetchUsers(p)}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size={isMobile ? 'small' : 'medium'}
                      sx={{ '& .MuiPaginationItem-root': { fontWeight: 600 } }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 1: User Prompt History — pick a user */}
            {tab === 1 && (
              <Box sx={{ p: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                    bgcolor: alpha(theme.palette.background.default, 0.5)
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1.5}
                    flexWrap="wrap"
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.main
                        }}
                      >
                        <Insights fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          User Prompt History
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Top active users in the last 7 days. Click any row to open full usage
                          analytics &amp; prompt history.
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      icon={<MouseOutlined sx={{ fontSize: '14px !important' }} />}
                      label="Click row to drill in"
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        color: 'text.secondary',
                        borderColor: alpha(theme.palette.divider, 1),
                        display: { xs: 'none', sm: 'flex' }
                      }}
                    />
                  </Box>
                </Paper>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                    overflow: 'hidden'
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['User', 'Plan', 'Role', 'Requests (7d)', 'Tokens (7d)', 'Blocked'].map(
                          (h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: 'text.secondary',
                                bgcolor: alpha(theme.palette.background.default, 0.9),
                                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                py: 1.75
                              }}
                            >
                              {h}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overviewLoading
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                              {[1, 2, 3, 4, 5, 6].map((j) => (
                                <TableCell key={j}>
                                  <Skeleton height={24} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        : (overview?.top_users_last_7_days || []).map((u, idx) => (
                            <TableRow
                              key={u.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                bgcolor:
                                  idx % 2 === 0
                                    ? 'transparent'
                                    : alpha(theme.palette.background.default, 0.45),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                                  '& .drill-arrow': { opacity: 1, transform: 'translateX(2px)' }
                                },
                                transition: 'background 0.15s'
                              }}
                              onClick={() => openUsage(u.id)}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                                      color: theme.palette.primary.main,
                                      fontSize: 13,
                                      fontWeight: 700
                                    }}
                                  >
                                    {u.email[0].toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={600}>
                                    {u.email}
                                  </Typography>
                                  <KeyboardArrowRight
                                    className="drill-arrow"
                                    sx={{
                                      fontSize: 18,
                                      color: theme.palette.primary.main,
                                      opacity: 0,
                                      transition: 'all 0.2s',
                                      ml: 'auto'
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={u.plan || 'free'}
                                  size="small"
                                  color={PLAN_CHIP_COLOR[u.plan] || 'default'}
                                  sx={{ fontWeight: 700, height: 22, fontSize: '0.72rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={getRoleIcon(u.role)}
                                  label={u.role.replace(/_/g, ' ')}
                                  color={getRoleColor(u.role)}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    height: 22,
                                    fontSize: '0.72rem',
                                    textTransform: 'capitalize'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={700} color="primary.main">
                                  {Number(u.requests || 0).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {Number(u.tokens || 0).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {Number(u.blocked) > 0 ? (
                                  <Chip
                                    label={u.blocked}
                                    size="small"
                                    color="error"
                                    sx={{ fontWeight: 700, height: 22 }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1.5, display: 'block', fontWeight: 600 }}
                >
                  Showing top active users. Click any row to open full usage detail &amp; prompt
                  history.
                </Typography>
              </Box>
            )}

            {/* Tab 2: User Usage Detail */}
            {tab === 2 && (
              <Box sx={{ p: 3 }}>
                {usageLoading ? (
                  <Box>
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 3 }} />
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Grid item xs={6} md={2} key={i}>
                          <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                  </Box>
                ) : selectedUser ? (
                  <UserUsageDetail usage={selectedUser} onBack={() => setTab(0)} theme={theme} />
                ) : null}
              </Box>
            )}
          </Paper>
        </Fade>
      </Box>

      {/* Plan Change Dialog */}
      <Dialog
        open={!!planDialog?.open}
        onClose={() => setPlanDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.15),
                color: theme.palette.warning.main
              }}
            >
              <Edit />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Change User Plan
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {planDialog?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.default, 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 1)}`
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Current Plan
            </Typography>
            <Box mt={0.75} display="flex" alignItems="center" gap={1}>
              <Chip
                label={planDialog?.currentPlan || 'free'}
                color={PLAN_CHIP_COLOR[planDialog?.currentPlan || 'free'] || 'default'}
                sx={{ fontWeight: 700, textTransform: 'capitalize' }}
              />
              <Typography variant="body2" color="text.secondary">
                {planDialog?.email}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
            sx={{
              mb: 1.5,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.75rem'
            }}
          >
            Select New Plan
          </Typography>
          <Grid container spacing={1.5}>
            {[
              {
                value: 'free',
                label: 'Free',
                desc: 'Basic access',
                icon: <MoneyOff />,
                color: theme.palette.text.secondary
              },
              {
                value: 'paid',
                label: 'Paid',
                desc: 'Full access',
                icon: <Star />,
                color: theme.palette.warning.main
              },
              {
                value: 'enterprise',
                label: 'Enterprise',
                desc: 'Unlimited',
                icon: <WorkspacePremium />,
                color: theme.palette.primary.main
              }
            ].map((p) => (
              <Grid item xs={4} key={p.value}>
                <Paper
                  variant="outlined"
                  onClick={() => setNewPlan(p.value)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${newPlan === p.value ? p.color : alpha(theme.palette.divider, 1)}`,
                    bgcolor: newPlan === p.value ? alpha(p.color, 0.08) : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': { border: `2px solid ${p.color}`, bgcolor: alpha(p.color, 0.06) }
                  }}
                >
                  <Box sx={{ color: newPlan === p.value ? p.color : 'text.secondary', mb: 0.5 }}>
                    {p.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: newPlan === p.value ? p.color : 'text.primary' }}
                  >
                    {p.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPlanDialog(null)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePlanUpdate}
            disabled={!newPlan || planUpdating}
            startIcon={
              planUpdating ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />
            }
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {planUpdating ? 'Updating...' : 'Update Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Quota Dialog */}
      <Dialog
        open={!!quotaDialog?.open}
        onClose={() => setQuotaDialog(null)}
        TransitionComponent={Zoom}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                color: theme.palette.warning.main,
                width: 40,
                height: 40
              }}
            >
              <Token />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Set Monthly Token Quota
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {quotaDialog?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {quotaDialog && (
            <>
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
                  {quotaDialog.countFrom
                    ? `Usage since quota was set (${new Date(quotaDialog.countFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
                    : "This month's usage"}
                </Typography>
                <Typography variant="h5" fontWeight={800} color="warning.main">
                  {quotaDialog.usedThisMonth.toLocaleString()} tokens
                </Typography>
                {quotaDialog.currentQuota && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        (quotaDialog.usedThisMonth / quotaDialog.currentQuota) * 100
                      )}
                      sx={{
                        mt: 1,
                        borderRadius: 1,
                        height: 6,
                        bgcolor: alpha(theme.palette.warning.main, 0.15),
                        '& .MuiLinearProgress-bar': {
                          bgcolor:
                            quotaDialog.usedThisMonth >= quotaDialog.currentQuota
                              ? theme.palette.error.main
                              : theme.palette.warning.main
                        }
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={
                        quotaDialog.usedThisMonth >= quotaDialog.currentQuota
                          ? 'error'
                          : 'text.secondary'
                      }
                      sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      {quotaDialog.usedThisMonth.toLocaleString()} /{' '}
                      {quotaDialog.currentQuota.toLocaleString()} used
                      {quotaDialog.usedThisMonth >= quotaDialog.currentQuota && ' — LIMIT REACHED'}
                    </Typography>
                  </>
                )}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Resets{' '}
                  {new Date(quotaDialog.resetsAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Monthly Token Limit"
                type="number"
                value={newQuota}
                onChange={(e) => setNewQuota(e.target.value)}
                placeholder="e.g. 100000"
                helperText="Set to 0 or leave empty to remove the limit (unlimited)"
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Token fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setQuotaDialog(null)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleQuotaUpdate}
            disabled={quotaUpdating}
            startIcon={quotaUpdating ? <CircularProgress size={16} color="inherit" /> : <Token />}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {quotaUpdating ? 'Saving...' : parseInt(newQuota) > 0 ? 'Set Quota' : 'Remove Limit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Sub-component: User Usage Detail
const UserUsageDetail: React.FC<{ usage: UserUsage; onBack: () => void; theme: any }> = ({
  usage,
  onBack,
  theme
}) => {
  const { user, overall, last_7_days, model_breakdown, daily_activity, api_keys } = usage;
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPages, setActivityPages] = useState(1);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    const fetchActivity = async (page = 1) => {
      setActivityLoading(true);
      try {
        const { data } = await axios.get(
          `/api/super-admin/users/${user.id}/activity?days=7&limit=20&page=${page}`
        );
        setActivity(data.activity || []);
        setActivityTotal(data.pagination?.total || 0);
        setActivityPages(data.pagination?.pages || 1);
      } catch {
        // silently fail — usage data still shows
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity(activityPage);
  }, [user.id, activityPage]);

  const statCards = [
    {
      label: 'Total Requests',
      sublabel: '30 days',
      value: Number(overall.total_requests || 0).toLocaleString(),
      icon: <TrendingUp />,
      color: theme.palette.primary.main
    },
    {
      label: 'Total Tokens',
      sublabel: '30 days',
      value: Number(overall.total_tokens || 0).toLocaleString(),
      icon: <Token />,
      color: theme.palette.info.main
    },
    {
      label: 'Blocked',
      sublabel: '30 days',
      value: Number(overall.blocked || 0).toLocaleString(),
      icon: <Block />,
      color: theme.palette.error.main
    },
    {
      label: 'Flagged',
      sublabel: '30 days',
      value: Number(overall.flagged || 0).toLocaleString(),
      icon: <Flag />,
      color: theme.palette.warning.main
    },
    {
      label: 'Active Days',
      sublabel: '30 days',
      value: Number(overall.active_days || 0).toLocaleString(),
      icon: <Timeline />,
      color: theme.palette.success.main
    },
    {
      label: 'API Keys Used',
      sublabel: '30 days',
      value: Number(overall.unique_api_keys_used || 0).toLocaleString(),
      icon: <Api />,
      color: theme.palette.secondary.main
    },
    {
      label: 'Requests (7d)',
      sublabel: 'Last 7 days',
      value: Number(last_7_days.total_requests || 0).toLocaleString(),
      icon: <Speed />,
      color: theme.palette.primary.light
    },
    {
      label: 'Avg Response',
      sublabel: 'ms',
      value:
        overall.avg_processing_ms && Number(overall.avg_processing_ms) > 0
          ? `${Math.round(Number(overall.avg_processing_ms))}ms`
          : '—',
      icon: <Assessment />,
      color: theme.palette.text.secondary
    }
  ];

  return (
    <Box>
      {/* User Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 1)}`,
          bgcolor: alpha(theme.palette.background.default, 0.5)
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              borderColor: alpha(theme.palette.divider, 1),
              color: 'text.secondary',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.06)
              }
            }}
          >
            Back
          </Button>
          <Divider orientation="vertical" flexItem />
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: 18
            }}
          >
            {user.email[0].toUpperCase()}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {user.email}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
              <Chip
                icon={getRoleIcon(user.role)}
                label={user.role.replace(/_/g, ' ')}
                color={getRoleColor(user.role)}
                size="small"
                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
              />
              <Chip
                label={user.plan}
                size="small"
                color={PLAN_CHIP_COLOR[user.plan] || 'default'}
                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
              />
              <Chip
                label={user.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={user.is_active ? 'success' : 'default'}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              {user.last_login && (
                <Chip
                  label={`Last login: ${new Date(user.last_login).toLocaleDateString()}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          {/* Threat Score Badge */}
          <Box
            sx={{
              textAlign: 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 2.5,
              border: `2px solid ${
                parseFloat(String(overall.avg_threat_score || 0)) > 0.5
                  ? alpha(theme.palette.error.main, 0.4)
                  : alpha(theme.palette.success.main, 0.4)
              }`,
              bgcolor:
                parseFloat(String(overall.avg_threat_score || 0)) > 0.5
                  ? alpha(theme.palette.error.main, 0.07)
                  : alpha(theme.palette.success.main, 0.07),
              minWidth: 100
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}
            >
              Avg Threat
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color:
                  parseFloat(String(overall.avg_threat_score || 0)) > 0.5
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                lineHeight: 1.2,
                mt: 0.25
              }}
            >
              {parseFloat(String(overall.avg_threat_score || 0)).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              max: {parseFloat(String(overall.max_threat_score || 0)).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s, i) => (
          <Grid item xs={6} sm={4} md={3} key={s.label}>
            <Grow in timeout={400 + i * 80}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(s.color, 0.06)} 100%)`,
                  border: `1px solid ${alpha(s.color, 0.15)}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 16px ${alpha(s.color, 0.15)}`
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ color: s.color, mb: 1 }}>{s.icon}</Box>
                  <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>
                    {s.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: 'block' }}
                  >
                    {s.label}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    {s.sublabel}
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Daily Activity */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 1)}` }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main
                }}
              >
                <Timeline fontSize="small" />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700}>
                Last 7 Days Activity
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Requests', 'Tokens', 'Blocked', 'Flagged', 'Threat Score'].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            color: 'text.secondary',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            pb: 1
                          }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daily_activity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No activity in last 7 days
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    daily_activity.map((d) => (
                      <TableRow
                        key={d.date}
                        hover
                        sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {new Date(d.date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {Number(d.requests).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {Number(d.tokens || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={d.blocked}
                            size="small"
                            color={d.blocked > 0 ? 'error' : 'default'}
                            variant={d.blocked > 0 ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={d.flagged}
                            size="small"
                            color={d.flagged > 0 ? 'warning' : 'default'}
                            variant={d.flagged > 0 ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              color:
                                parseFloat(String(d.avg_threat_score)) > 0.5
                                  ? theme.palette.error.main
                                  : theme.palette.success.main
                            }}
                          >
                            {parseFloat(String(d.avg_threat_score || 0)).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={5}>
          {/* API Keys */}
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 1)}` }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  color: theme.palette.info.main
                }}
              >
                <Security fontSize="small" />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700}>
                API Keys
              </Typography>
            </Box>
            {api_keys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No API keys
              </Typography>
            ) : (
              api_keys.map((k) => (
                <Box
                  key={k.id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                    bgcolor: alpha(theme.palette.background.default, 0.5)
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {k.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {k.usage_count.toLocaleString()} uses · {k.rate_limit}/hr limit
                      </Typography>
                      {k.last_used && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block' }}
                        >
                          Last used: {new Date(k.last_used).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={k.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={k.is_active ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Prompt History — last 7 days */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 1)}`
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2.5}
          flexWrap="wrap"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                color: theme.palette.warning.main
              }}
            >
              <Timeline fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Prompt History — Last 7 Days
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activityTotal} total prompts · what this user searched &amp; sent
              </Typography>
            </Box>
          </Box>
          {activityPages > 1 && (
            <Pagination
              count={activityPages}
              page={activityPage}
              onChange={(_, p) => setActivityPage(p)}
              size="small"
              color="primary"
              sx={{ '& .MuiPaginationItem-root': { fontWeight: 600 } }}
            />
          )}
        </Box>

        {activityLoading ? (
          <Box>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={52} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : activity.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                mx: 'auto',
                mb: 1.5
              }}
            >
              <Timeline sx={{ fontSize: 32, color: alpha(theme.palette.text.secondary, 0.4) }} />
            </Avatar>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              No prompt activity in the last 7 days
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['', 'Prompt', 'Model / Endpoint', 'Status', 'Tokens', 'Threat', 'Time'].map(
                    (h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          color: 'text.secondary',
                          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          pb: 1.25,
                          whiteSpace: 'nowrap',
                          ...(h === '' && { width: 36, px: 1 })
                        }}
                      >
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {activity.map((a, idx) => {
                  const statusColor: Record<string, any> = {
                    blocked: 'error',
                    flagged: 'warning',
                    allowed: 'success',
                    redacted: 'info'
                  };
                  const isExpanded = expandedRow === a.id;
                  return (
                    <React.Fragment key={a.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedRow(isExpanded ? null : a.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor:
                            idx % 2 === 0
                              ? 'transparent'
                              : alpha(theme.palette.background.default, 0.4),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                          transition: 'background 0.15s'
                        }}
                      >
                        {/* Expand chevron */}
                        <TableCell sx={{ py: 1.25, px: 1, width: 36 }}>
                          <IconButton
                            size="small"
                            tabIndex={-1}
                            sx={{ p: 0.25, color: 'text.disabled' }}
                          >
                            {isExpanded ? (
                              <ExpandLess sx={{ fontSize: 18 }} />
                            ) : (
                              <ExpandMore sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </TableCell>

                        {/* Prompt text */}
                        <TableCell sx={{ maxWidth: 260, py: 1.25 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: isExpanded ? 'normal' : 'nowrap',
                              maxWidth: 260,
                              fontWeight: 500,
                              wordBreak: 'break-word'
                            }}
                          >
                            {a.prompt_text || (
                              <span
                                style={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}
                              >
                                —
                              </span>
                            )}
                          </Typography>
                          {isExpanded && (
                            <Box
                              sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
                            >
                              {/* Response */}
                              {a.response_text && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: alpha(theme.palette.success.main, 0.06),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="success.main"
                                    fontWeight={700}
                                    sx={{
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.4px',
                                      display: 'block',
                                      mb: 0.5
                                    }}
                                  >
                                    Response
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      wordBreak: 'break-word',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    {a.response_text.length > 400
                                      ? a.response_text.slice(0, 400) + '…'
                                      : a.response_text}
                                  </Typography>
                                </Box>
                              )}
                              {/* Filtered/redacted prompt */}
                              {a.filtered_prompt && a.filtered_prompt !== a.prompt_text && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: alpha(theme.palette.info.main, 0.06),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="info.main"
                                    fontWeight={700}
                                    sx={{
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.4px',
                                      display: 'block',
                                      mb: 0.5
                                    }}
                                  >
                                    Redacted Prompt
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}
                                  >
                                    {a.filtered_prompt.length > 300
                                      ? a.filtered_prompt.slice(0, 300) + '…'
                                      : a.filtered_prompt}
                                  </Typography>
                                </Box>
                              )}
                              {/* Detected threats */}
                              {a.detected_threats && a.detected_threats.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {a.detected_threats.map((t: any, ti: number) => {
                                    const label =
                                      typeof t === 'string'
                                        ? t
                                        : t?.rule || t?.type || JSON.stringify(t);
                                    return (
                                      <Chip
                                        key={ti}
                                        label={label}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                              {/* PII info */}
                              {a.pii_redacted && (
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="warning.main"
                                    fontWeight={700}
                                  >
                                    PII Detected &amp; Redacted
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', fontSize: '0.65rem' }}
                                  >
                                    {typeof a.pii_redacted === 'object'
                                      ? JSON.stringify(a.pii_redacted)
                                      : String(a.pii_redacted)}
                                  </Typography>
                                </Box>
                              )}
                              {/* Meta row */}
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {a.processing_time_ms != null && (
                                  <Typography variant="caption" color="text.disabled">
                                    ⏱ {a.processing_time_ms}ms
                                  </Typography>
                                )}
                                {a.api_key_name && (
                                  <Typography variant="caption" color="text.disabled">
                                    🔑 {a.api_key_name}
                                  </Typography>
                                )}
                                {a.endpoint_used && (
                                  <Typography variant="caption" color="text.disabled">
                                    📡 {a.endpoint_used}
                                  </Typography>
                                )}
                                {a.request_ip && (
                                  <Tooltip title={a.user_agent || 'No user agent'} arrow>
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ fontFamily: 'monospace', cursor: 'help' }}
                                    >
                                      🌐 {a.request_ip}
                                    </Typography>
                                  </Tooltip>
                                )}
                                <Typography variant="caption" color="text.disabled">
                                  💰 ${a.estimated_cost_usd.toFixed(5)}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </TableCell>
                        {/* Model */}
                        <TableCell sx={{ py: 1.25 }}>
                          {a.model ? (
                            <Chip
                              label={a.model}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                height: 22,
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            />
                          ) : (
                            <Tooltip
                              title={
                                a.status === 'blocked'
                                  ? 'Blocked before reaching LLM'
                                  : 'Model not recorded (demo/simulated path)'
                              }
                              arrow
                              placement="top"
                            >
                              <Chip
                                label={a.status === 'blocked' ? 'pre-LLM block' : 'simulated'}
                                size="small"
                                variant="outlined"
                                color={a.status === 'blocked' ? 'error' : 'default'}
                                sx={{
                                  fontWeight: 600,
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontStyle: 'italic',
                                  opacity: 0.75
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        {/* Status */}
                        <TableCell sx={{ py: 1.25 }}>
                          <Chip
                            label={a.status}
                            size="small"
                            color={statusColor[a.status] || 'default'}
                            sx={{
                              fontWeight: 700,
                              height: 22,
                              fontSize: '0.7rem',
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        {/* Tokens */}
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {Number(a.tokens_used || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        {/* Threat score */}
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color:
                                a.threat_score > 0.5
                                  ? theme.palette.error.main
                                  : a.threat_score > 0.2
                                    ? theme.palette.warning.main
                                    : theme.palette.success.main
                            }}
                          >
                            {parseFloat(String(a.threat_score || 0)).toFixed(2)}
                          </Typography>
                        </TableCell>
                        {/* Time */}
                        <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {new Date(a.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', fontSize: '0.65rem' }}
                          >
                            {new Date(a.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {activity.length > 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 1.5, display: 'block', textAlign: 'center', fontStyle: 'italic' }}
          >
            Click any row to expand prompt details, response, and metadata
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default SuperAdminDashboard;
